import { supabase } from '../lib/supabase';

// TypeScript interfaces for Career Journey
export interface CareerMilestone {
  id: string;
  userId: string;
  roleTitle: string;
  companyName: string;
  companyLogoUrl?: string;
  description?: string;
  startDate: string;
  endDate?: string;
  isCurrent: boolean;
  milestoneType: 'promotion' | 'role_change' | 'major_achievement' | 'certification' | 'award' | 'project_completion';
  visibilityStatus: 'public' | 'private' | 'draft';
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
  achievements?: Achievement[];
}

export interface Achievement {
  id: string;
  milestoneId: string;
  achievementTitle: string;
  description?: string;
  impactMetric?: string;
  beforeValue?: string;
  afterValue?: string;
  roiCalculation?: string;
  displayOrder: number;
  createdAt: string;
}

// Database row types (snake_case)
interface MilestoneRow {
  id: string;
  user_id: string;
  role_title: string;
  company_name: string;
  company_logo_url?: string;
  description?: string;
  start_date: string;
  end_date?: string;
  is_current: boolean;
  milestone_type: string;
  visibility_status: string;
  display_order: number;
  created_at: string;
  updated_at: string;
}

interface AchievementRow {
  id: string;
  milestone_id: string;
  achievement_title: string;
  description?: string;
  impact_metric?: string;
  before_value?: string;
  after_value?: string;
  roi_calculation?: string;
  display_order: number;
  created_at: string;
}

// Conversion functions
const convertMilestoneFromDB = (row: MilestoneRow): CareerMilestone => ({
  id: row.id,
  userId: row.user_id,
  roleTitle: row.role_title,
  companyName: row.company_name,
  companyLogoUrl: row.company_logo_url,
  description: row.description,
  startDate: row.start_date,
  endDate: row.end_date,
  isCurrent: row.is_current,
  milestoneType: row.milestone_type as CareerMilestone['milestoneType'],
  visibilityStatus: row.visibility_status as CareerMilestone['visibilityStatus'],
  displayOrder: row.display_order,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

const convertAchievementFromDB = (row: AchievementRow): Achievement => ({
  id: row.id,
  milestoneId: row.milestone_id,
  achievementTitle: row.achievement_title,
  description: row.description,
  impactMetric: row.impact_metric,
  beforeValue: row.before_value,
  afterValue: row.after_value,
  roiCalculation: row.roi_calculation,
  displayOrder: row.display_order,
  createdAt: row.created_at,
});

export const careerJourneyService = {
  // Get all milestones for a user with achievements
  async getMilestones(userId: string): Promise<{ data: CareerMilestone[] | null; error: Error | null }> {
    try {
      const { data: milestoneData, error: milestoneError } = await supabase
        .from('career_milestones')
        .select('*')
        .eq('user_id', userId)
        .order('display_order', { ascending: true });

      if (milestoneError) throw milestoneError;

      if (!milestoneData || milestoneData.length === 0) {
        return { data: [], error: null };
      }

      // Fetch achievements for all milestones
      const milestoneIds = milestoneData.map((m) => m.id);
      const { data: achievementData, error: achievementError } = await supabase
        .from('milestone_achievements')
        .select('*')
        .in('milestone_id', milestoneIds)
        .order('display_order', { ascending: true });

      if (achievementError) throw achievementError;

      // Convert and group achievements by milestone
      const achievementsByMilestone = new Map<string, Achievement[]>();
      (achievementData || []).forEach((row) => {
        const achievement = convertAchievementFromDB(row);
        if (!achievementsByMilestone.has(achievement.milestoneId)) {
          achievementsByMilestone.set(achievement.milestoneId, []);
        }
        achievementsByMilestone.get(achievement.milestoneId)?.push(achievement);
      });

      // Convert milestones and attach achievements
      const milestones = milestoneData.map((row) => {
        const milestone = convertMilestoneFromDB(row);
        milestone.achievements = achievementsByMilestone.get(milestone.id) || [];
        return milestone;
      });

      return { data: milestones, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Create new milestone
  async createMilestone(
    milestone: Omit<CareerMilestone, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<{ data: CareerMilestone | null; error: Error | null }> {
    try {
      const insertData = {
        user_id: milestone.userId,
        role_title: milestone.roleTitle,
        company_name: milestone.companyName,
        company_logo_url: milestone.companyLogoUrl,
        description: milestone.description,
        start_date: milestone.startDate,
        end_date: milestone.endDate,
        is_current: milestone.isCurrent,
        milestone_type: milestone.milestoneType,
        visibility_status: milestone.visibilityStatus,
        display_order: milestone.displayOrder,
      };

      const { data, error } = await supabase
        .from('career_milestones')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;

      return { data: convertMilestoneFromDB(data), error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Update milestone
  async updateMilestone(
    id: string,
    updates: Partial<Omit<CareerMilestone, 'id' | 'userId' | 'createdAt' | 'updatedAt'>>
  ): Promise<{ data: CareerMilestone | null; error: Error | null }> {
    try {
      const updateData: Record<string, any> = {};
      if (updates.roleTitle !== undefined) updateData.role_title = updates.roleTitle;
      if (updates.companyName !== undefined) updateData.company_name = updates.companyName;
      if (updates.companyLogoUrl !== undefined) updateData.company_logo_url = updates.companyLogoUrl;
      if (updates.description !== undefined) updateData.description = updates.description;
      if (updates.startDate !== undefined) updateData.start_date = updates.startDate;
      if (updates.endDate !== undefined) updateData.end_date = updates.endDate;
      if (updates.isCurrent !== undefined) updateData.is_current = updates.isCurrent;
      if (updates.milestoneType !== undefined) updateData.milestone_type = updates.milestoneType;
      if (updates.visibilityStatus !== undefined) updateData.visibility_status = updates.visibilityStatus;
      if (updates.displayOrder !== undefined) updateData.display_order = updates.displayOrder;

      const { data, error } = await supabase
        .from('career_milestones')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      return { data: convertMilestoneFromDB(data), error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Delete milestone (cascades to achievements)
  async deleteMilestone(id: string): Promise<{ error: Error | null }> {
    try {
      const { error } = await supabase.from('career_milestones').delete().eq('id', id);

      if (error) throw error;

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  },

  // Create achievement
  async createAchievement(
    achievement: Omit<Achievement, 'id' | 'createdAt'>
  ): Promise<{ data: Achievement | null; error: Error | null }> {
    try {
      const insertData = {
        milestone_id: achievement.milestoneId,
        achievement_title: achievement.achievementTitle,
        description: achievement.description,
        impact_metric: achievement.impactMetric,
        before_value: achievement.beforeValue,
        after_value: achievement.afterValue,
        roi_calculation: achievement.roiCalculation,
        display_order: achievement.displayOrder,
      };

      const { data, error } = await supabase
        .from('milestone_achievements')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;

      return { data: convertAchievementFromDB(data), error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Update achievement
  async updateAchievement(
    id: string,
    updates: Partial<Omit<Achievement, 'id' | 'milestoneId' | 'createdAt'>>
  ): Promise<{ data: Achievement | null; error: Error | null }> {
    try {
      const updateData: Record<string, any> = {};
      if (updates.achievementTitle !== undefined) updateData.achievement_title = updates.achievementTitle;
      if (updates.description !== undefined) updateData.description = updates.description;
      if (updates.impactMetric !== undefined) updateData.impact_metric = updates.impactMetric;
      if (updates.beforeValue !== undefined) updateData.before_value = updates.beforeValue;
      if (updates.afterValue !== undefined) updateData.after_value = updates.afterValue;
      if (updates.roiCalculation !== undefined) updateData.roi_calculation = updates.roiCalculation;
      if (updates.displayOrder !== undefined) updateData.display_order = updates.displayOrder;

      const { data, error } = await supabase
        .from('milestone_achievements')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      return { data: convertAchievementFromDB(data), error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Delete achievement
  async deleteAchievement(id: string): Promise<{ error: Error | null }> {
    try {
      const { error } = await supabase.from('milestone_achievements').delete().eq('id', id);

      if (error) throw error;

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  },

  // Bulk update display order
  async updateDisplayOrder(
    milestones: { id: string; displayOrder: number }[]
  ): Promise<{ error: Error | null }> {
    try {
      const updates = milestones.map((m) =>
        supabase.from('career_milestones').update({ display_order: m.displayOrder }).eq('id', m.id)
      );

      const results = await Promise.all(updates);
      const errors = results.filter((r) => r.error);

      if (errors.length > 0) {
        throw new Error(`Failed to update ${errors.length} milestone(s)`);
      }

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  },

  // Get public milestones for About page (without requiring userId)
  async getAllPublicMilestones(): Promise<{ data: CareerMilestone[] | null; error: Error | null }> {
    try {
      const { data: milestoneData, error: milestoneError } = await supabase
        .from('career_milestones')
        .select('*')
        .eq('visibility_status', 'public')
        .order('display_order', { ascending: true });

      if (milestoneError) throw milestoneError;

      if (!milestoneData || milestoneData.length === 0) {
        return { data: [], error: null };
      }

      // Fetch achievements for public milestones
      const milestoneIds = milestoneData.map((m) => m.id);
      const { data: achievementData, error: achievementError } = await supabase
        .from('milestone_achievements')
        .select('*')
        .in('milestone_id', milestoneIds)
        .order('display_order', { ascending: true });

      if (achievementError) throw achievementError;

      // Group achievements by milestone
      const achievementsByMilestone = new Map<string, Achievement[]>();
      (achievementData || []).forEach((row) => {
        const achievement = convertAchievementFromDB(row);
        if (!achievementsByMilestone.has(achievement.milestoneId)) {
          achievementsByMilestone.set(achievement.milestoneId, []);
        }
        achievementsByMilestone.get(achievement.milestoneId)?.push(achievement);
      });

      // Convert and attach achievements
      const milestones = milestoneData.map((row) => {
        const milestone = convertMilestoneFromDB(row);
        milestone.achievements = achievementsByMilestone.get(milestone.id) || [];
        return milestone;
      });

      return { data: milestones, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Get public milestones for About page
  async getPublicMilestones(userId: string): Promise<{ data: CareerMilestone[] | null; error: Error | null }> {
    try {
      const { data: milestoneData, error: milestoneError } = await supabase
        .from('career_milestones')
        .select('*')
        .eq('user_id', userId)
        .eq('visibility_status', 'public')
        .order('display_order', { ascending: true });

      if (milestoneError) throw milestoneError;

      if (!milestoneData || milestoneData.length === 0) {
        return { data: [], error: null };
      }

      // Fetch achievements for public milestones
      const milestoneIds = milestoneData.map((m) => m.id);
      const { data: achievementData, error: achievementError } = await supabase
        .from('milestone_achievements')
        .select('*')
        .in('milestone_id', milestoneIds)
        .order('display_order', { ascending: true });

      if (achievementError) throw achievementError;

      // Group achievements by milestone
      const achievementsByMilestone = new Map<string, Achievement[]>();
      (achievementData || []).forEach((row) => {
        const achievement = convertAchievementFromDB(row);
        if (!achievementsByMilestone.has(achievement.milestoneId)) {
          achievementsByMilestone.set(achievement.milestoneId, []);
        }
        achievementsByMilestone.get(achievement.milestoneId)?.push(achievement);
      });

      // Convert and attach achievements
      const milestones = milestoneData.map((row) => {
        const milestone = convertMilestoneFromDB(row);
        milestone.achievements = achievementsByMilestone.get(milestone.id) || [];
        return milestone;
      });

      return { data: milestones, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },
};